import React, { useMemo, useState } from 'react';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import { runDesignChecks, SEVERITY } from '@/lib/designChecks';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AlertCircle, AlertTriangle, Info, ShieldCheck, ChevronRight } from 'lucide-react';

const SEV_META = {
  [SEVERITY.ERROR]: { color: '#B42318', Icon: AlertCircle, sectionKey: 'dc_section_errors' },
  [SEVERITY.WARNING]: { color: '#B7791F', Icon: AlertTriangle, sectionKey: 'dc_section_warnings' },
  [SEVERITY.INFO]: { color: '#5B6672', Icon: Info, sectionKey: 'dc_section_infos' },
};

const SummaryPill = ({ color, count, Icon, testid }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-semibold tabular-nums"
    style={{ borderColor: `${color}40`, color, background: `${color}0F` }}
    data-testid={testid}
  >
    <Icon className="h-3.5 w-3.5" /> {count}
  </span>
);

export const DesignCheckPanel = ({ onFocusVlan }) => {
  const { t, language } = useI18n();
  const open = useStore((s) => s.designCheckOpen);
  const setOpen = useStore((s) => s.setDesignCheckOpen);
  const doc = useStore((s) => s.doc);
  const focusNode = useStore((s) => s.focusNode);
  const [showUnconnected, setShowUnconnected] = useState(false);

  const { findings, counts } = useMemo(
    () => runDesignChecks(doc, { lang: language, includeInfo: true, includeUnconnected: showUnconnected }),
    [doc, language, showUnconnected]
  );

  const groups = useMemo(() => {
    const g = { error: [], warning: [], info: [] };
    findings.forEach((f) => g[f.severity].push(f));
    return g;
  }, [findings]);

  const onFocus = (f) => {
    const dev = f.targets.find((tg) => tg.type === 'device');
    const vlan = f.targets.find((tg) => tg.type === 'vlan');
    if (dev) {
      focusNode(dev.id);
      setOpen(false);
    } else if (vlan) {
      setOpen(false);
      onFocusVlan?.();
    }
  };

  const total = counts.error + counts.warning + counts.info;

  const renderSection = (severity) => {
    const list = groups[severity];
    if (!list.length) return null;
    const meta = SEV_META[severity];
    return (
      <div className="space-y-1.5" data-testid={`dc-section-${severity}`}>
        <div className="flex items-center gap-2 px-1">
          <meta.Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
          <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: meta.color }}>
            {t(meta.sectionKey)}
          </span>
          <span className="text-[11px] tabular-nums text-muted-foreground">· {list.length}</span>
        </div>
        <div className="space-y-1">
          {list.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onFocus(f)}
              data-testid={`dc-finding-${f.code}`}
              className="group flex w-full items-start gap-2 rounded-md border border-border bg-white px-2.5 py-2 text-left transition-colors duration-150 hover:border-[#0B5FFF]/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B5FFF]/40"
            >
              <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full" style={{ background: meta.color }} />
              <span className="min-w-0 flex-1 text-[12.5px] leading-snug text-[#2A3138]">{t(`dc_${f.code}`, f.params)}</span>
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-[88vw] flex-col gap-0 p-0 sm:max-w-[420px]" data-testid="design-check-panel">
        <SheetHeader className="space-y-1 border-b border-border px-4 py-3 text-left">
          <SheetTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4.5 w-4.5 text-[#0B5FFF]" /> {t('design_check_title')}
          </SheetTitle>
          <SheetDescription className="text-xs">{t('design_check_desc')}</SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-2 border-b border-border bg-[#FBFBFA] px-4 py-2.5">
          <SummaryPill color="#B42318" count={counts.error} Icon={AlertCircle} testid="dc-count-errors" />
          <SummaryPill color="#B7791F" count={counts.warning} Icon={AlertTriangle} testid="dc-count-warnings" />
          <SummaryPill color="#5B6672" count={counts.info} Icon={Info} testid="dc-count-infos" />
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto px-4 py-4">
          {total === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-14 text-center" data-testid="dc-empty">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0F7B6C]/10 text-[#0F7B6C]">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-foreground">{t('design_check_ok')}</p>
              <p className="text-xs text-muted-foreground">{t('design_check_ok_hint')}</p>
            </div>
          ) : (
            <>
              {renderSection(SEVERITY.ERROR)}
              {renderSection(SEVERITY.WARNING)}
              {renderSection(SEVERITY.INFO)}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-border bg-[#FBFBFA] px-4 py-3">
          <Switch id="dc-unconnected" checked={showUnconnected} onCheckedChange={setShowUnconnected} data-testid="dc-show-unconnected-switch" />
          <Label htmlFor="dc-unconnected" className="cursor-pointer text-xs text-[#2A3138]">
            {t('dc_show_unconnected')}
          </Label>
        </div>
      </SheetContent>
    </Sheet>
  );
};
